import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Smartphone, CreditCard, Globe, Save, Trash2, Eye, EyeOff, Loader2, Zap } from 'lucide-react';
import { paymentApisAPI } from '../lib/api';
import { toast } from 'sonner';

const PROVIDER_ICONS = {
  mobile_money: Smartphone,
  card: CreditCard,
  aggregator: Globe,
};

const FIELD_LABELS = {
  merchant_number: 'Numéro marchand',
  merchant_name: 'Nom du marchand',
  consumer_key: 'Consumer Key',
  consumer_secret: 'Consumer Secret',
  api_key: 'API Key',
  api_secret: 'API Secret',
  webhook_secret: 'Webhook Secret',
  secret_key: 'Secret Key',
  publishable_key: 'Publishable Key',
  callback_url: 'Callback URL',
  notify_url: 'Notify URL',
  site_id: 'Site ID',
  environment: 'Environnement',
};

const PROVIDER_COLORS = {
  orange_money: 'from-orange-500 to-orange-600',
  wave: 'from-sky-500 to-sky-600',
  moov_money: 'from-blue-500 to-blue-600',
  sama_money: 'from-indigo-500 to-indigo-600',
  stripe: 'from-violet-500 to-violet-600',
  cinetpay: 'from-emerald-500 to-emerald-600',
};

export const PaymentApisTab = () => {
  const [catalog, setCatalog] = useState([]);
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [catRes, listRes] = await Promise.all([
          paymentApisAPI.getCatalog(),
          paymentApisAPI.list(),
        ]);
        setCatalog(catRes.data.providers || []);
        const map = {};
        (listRes.data.items || []).forEach((item) => {
          map[item.provider] = {
            is_active: item.is_active,
            config: item.config || {},
          };
        });
        setConfigs(map);
      } catch (e) {
        toast.error("Impossible de charger les API de paiement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getConfig = (provider) => configs[provider] || { is_active: false, config: {} };

  const updateField = (provider, field, value) => {
    setConfigs((prev) => ({
      ...prev,
      [provider]: {
        ...getConfig(provider),
        config: { ...getConfig(provider).config, [field]: value },
      },
    }));
  };

  const toggleActive = (provider, val) => {
    setConfigs((prev) => ({
      ...prev,
      [provider]: { ...getConfig(provider), is_active: val },
    }));
  };

  const toggleSecret = (provider, field) => {
    setShowSecrets((prev) => ({ ...prev, [`${provider}-${field}`]: !prev[`${provider}-${field}`] }));
  };

  const handleSave = async (provider) => {
    setSaving((p) => ({ ...p, [provider]: true }));
    try {
      const current = getConfig(provider);
      const { data } = await paymentApisAPI.save(provider, {
        is_active: current.is_active,
        config: current.config,
      });
      setConfigs((prev) => ({
        ...prev,
        [provider]: { is_active: data.is_active, config: data.config || {} },
      }));
      toast.success(`${catalog.find((p) => p.key === provider)?.label || provider} enregistré`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving((p) => ({ ...p, [provider]: false }));
    }
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`Supprimer la configuration de ${catalog.find((p) => p.key === provider)?.label} ?`)) return;
    try {
      await paymentApisAPI.remove(provider);
      setConfigs((prev) => {
        const { [provider]: _, ...rest } = prev;
        return rest;
      });
      toast.success('Configuration supprimée');
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="payment-apis-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payment-apis-tab">
      <Card className="border-l-4 border-l-[#B85C38] bg-gradient-to-br from-[#FBF6EE] to-white">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#B85C38] flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#2B3A2C]">Vos API de paiement</h3>
              <p className="text-sm text-slate-600 mt-1">
                Configurez les credentials de vos propres comptes marchands. Les secrets sont stockés
                chiffrés et jamais exposés en clair après enregistrement. Chaque API est isolée par agence.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {catalog.map((provider) => {
          const Icon = PROVIDER_ICONS[provider.category] || CreditCard;
          const current = getConfig(provider.key);
          const configured = !!current.config && Object.keys(current.config).some((k) => current.config[k]);
          return (
            <Card
              key={provider.key}
              className="relative overflow-hidden"
              data-testid={`payment-api-card-${provider.key}`}
            >
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${PROVIDER_COLORS[provider.key] || 'from-slate-400 to-slate-500'}`} />
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${PROVIDER_COLORS[provider.key] || 'from-slate-400 to-slate-500'} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{provider.label}</CardTitle>
                      <CardDescription className="text-xs capitalize">
                        {provider.category.replace('_', ' ')}
                        {configured && <span className="ml-2 text-emerald-600 font-medium">· Configuré</span>}
                      </CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={current.is_active}
                    onCheckedChange={(v) => toggleActive(provider.key, v)}
                    data-testid={`payment-api-toggle-${provider.key}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {provider.fields.map((field) => {
                  const isSecret = (provider.secret_fields || []).includes(field);
                  const isShown = showSecrets[`${provider.key}-${field}`];
                  const val = current.config?.[field] || '';

                  if (field === 'environment') {
                    return (
                      <div key={field}>
                        <Label className="text-xs">{FIELD_LABELS[field] || field}</Label>
                        <Select value={val || 'test'} onValueChange={(v) => updateField(provider.key, field, v)}>
                          <SelectTrigger data-testid={`payment-api-${provider.key}-${field}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="test">Test (sandbox)</SelectItem>
                            <SelectItem value="live">Live (production)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }

                  return (
                    <div key={field}>
                      <Label className="text-xs">{FIELD_LABELS[field] || field}</Label>
                      <div className="relative">
                        <Input
                          type={isSecret && !isShown ? 'password' : 'text'}
                          value={val}
                          onChange={(e) => updateField(provider.key, field, e.target.value)}
                          placeholder={isSecret ? '••••••••' : ''}
                          className={isSecret ? 'pr-10 font-mono text-sm' : ''}
                          data-testid={`payment-api-${provider.key}-${field}`}
                        />
                        {isSecret && (
                          <button
                            type="button"
                            onClick={() => toggleSecret(provider.key, field)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                            tabIndex={-1}
                          >
                            {isShown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleSave(provider.key)}
                    disabled={saving[provider.key]}
                    size="sm"
                    className="flex-1 bg-[#2B3A2C] hover:bg-[#1e2a20] text-white"
                    data-testid={`payment-api-save-${provider.key}`}
                  >
                    {saving[provider.key] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <><Save className="w-4 h-4 mr-1" /> Enregistrer</>
                    )}
                  </Button>
                  {configured && (
                    <Button
                      onClick={() => handleDelete(provider.key)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`payment-api-delete-${provider.key}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentApisTab;
